import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, NavigationEnd, RouterLink } from '@angular/router';
import { filter } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';
import { SessionContextService } from '../../core/services/session-context.service';
import { CompareOriginService } from '../../core/services/compare-origin.service';

interface NavSlot {
  id: string;
  label: string;
  route: string;
  visibleWhen?: () => boolean;
  hasDropdown?: boolean;
}

/**
 * Menu-axis Phase 2 — NavV3Component
 *
 * 44px horizontal app-nav. Seven primary slots + "More ▾" overflow.
 * Active-route highlighting via bottom border on matching slot.
 */
@Component({
  selector: 'app-nav-v3',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './nav-v3.component.html',
  styleUrl: './nav-v3.component.scss',
})
export class NavV3Component {
  private router = inject(Router);
  protected session = inject(SessionContextService);
  private compareOrigin = inject(CompareOriginService);

  protected readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
    ),
    { initialValue: null },
  );

  protected readonly primarySlots: NavSlot[] = [
    // { id: 'dashboard',    label: 'Dashboard',    route: '/dashboard' },
    { id: 'rates',        label: 'Rates',        route: '/rates' },
    // PRIMARY, not overflow — and that is forced rather than chosen. The
    // reference build keeps this in the More menu because promoting it is an
    // unmade layout decision, but in THIS shell the More button is commented
    // out (see nav-v3.component.html), so overflowSlots renders nothing and an
    // item placed there is unreachable. Primary slot or invisible were the two
    // options. Move it back the moment the overflow menu is restored.
    { id: 'changes',      label: 'Upcoming Rate Changes', route: '/changes' },
    // {
    //   id: 'rate-sheets',
    //   label: 'Rate Sheets',
    //   route: '/rate-sheets',
    //   visibleWhen: () => this.session.hasAnyModule([
    //     'fa_broker_rate_sheet', 'fa_std_broker_rate_sheet',
    //     'index_broker_rate_sheet', 'index_var_broker_rate_sheet',
    //   ]),
    // },
    { id: 'products',     label: 'Products',     route: '/products' },
    { id: 'calculator',   label: 'Calculator',   route: '/calculator',  hasDropdown: true },
    { id: 'cases',        label: 'Cases',        route: '/broker/cases',
      visibleWhen: () => this.session.casesEnabled() },
    { id: 'disclosure',   label: 'Disclosure',   route: '/disclosures' },
    // { id: 'market-study', label: 'Market Study', route: '/sales' },
    {
      id: 'admin',
      label: 'Admin',
      route: '/admin',
      // admin === 1 && !suppress_admin. Was isBeaconAdmin(), which also
      // required Beacon's own company AND passed when no session was loaded —
      // so the item showed for non-admins mid-load.
      visibleWhen: () => this.session.canAccessAdmin() && this.session.isBeaconUser(),
    },
  ];

  protected readonly overflowSlots: NavSlot[] = [
    { id: 'lookup',      label: 'Lookup',         route: '/lookup' },
    { id: 'watchlist',   label: 'Watchlist',      route: '/watchlist' },
    { id: 'inbox',       label: 'Inbox',          route: '/inbox' },
    { id: 'calculator-v3', label: 'Calculator V3 · Preview', route: '/calculator-v3' },
    {
      id: 'mockups',
      label: 'Mockups · Dev',
      route: '/mockups',
      visibleWhen: () => this.session.isBeaconAdmin(),
    },
    {
      id: 'demos',
      label: 'Demos · Dev',
      route: '/demos',
      visibleWhen: () => this.session.isBeaconAdmin(),
    },
  ];

  protected readonly visiblePrimary = computed(() =>
    this.primarySlots.filter(s => !s.visibleWhen || s.visibleWhen()),
  );

  protected readonly visibleOverflow = computed(() =>
    this.overflowSlots.filter(s => !s.visibleWhen || s.visibleWhen()),
  );

  protected readonly activeSlotId = computed<string | null>(() => {
    const ev = this.currentUrl();
    const url = ev?.urlAfterRedirects ?? this.router.url ?? '/';
    // Comparison surfaces sit under /calculator/compare and /compare/structural,
    // but the basket that feeds them is filled on Rates, Upcoming Rate Changes,
    // Products or Disclosure. Highlighting by URL would move the highlight to
    // Calculator from a page the user never left, so resolve the slot from the
    // page the comparison was launched from. Falls back to the URL when there
    // is no origin (a cold deep-link into a comparison).
    if (CompareOriginService.isCompareSurface(url)) {
      const fromOrigin = this.slotIdForUrl(this.compareOrigin.originUrl());
      if (fromOrigin) return fromOrigin;
    }
    if (url.startsWith('/calculator-v2') || url.startsWith('/calculator-v3')) {
      return 'calculator';
    }
    return this.slotIdForUrl(url);
  });

  /** Longest-route-wins match of a URL against the nav slots. */
  private slotIdForUrl(url: string | null): string | null {
    if (!url) return null;
    const all: NavSlot[] = [...this.primarySlots, ...this.overflowSlots];
    const sorted = [...all].sort((a, b) => b.route.length - a.route.length);
    const hit = sorted.find(s => url === s.route || url.startsWith(s.route + '/') || url.startsWith(s.route + '?'));
    return hit?.id ?? null;
  }

  protected readonly activeOverflowSlot = computed<NavSlot | null>(() => {
    const id = this.activeSlotId();
    return this.overflowSlots.find(s => s.id === id) ?? null;
  });

  protected readonly activeCalcVariant = computed<'V1' | 'V2' | 'V3' | null>(() => {
    const ev = this.currentUrl();
    const url = ev?.urlAfterRedirects ?? this.router.url ?? '';
    if (url.startsWith('/calculator-v3')) return 'V3';
    if (url.startsWith('/calculator-v2')) return 'V2';
    if (url.startsWith('/calculator')) return 'V1';
    return null;
  });

  protected readonly showDevContext = computed(() => this.session.isBeaconAdmin());

  protected readonly calcVariants: ReadonlyArray<{ id: 'V1' | 'V2' | 'V3'; label: string; route: string }> = [
    { id: 'V1', label: 'Calculator',              route: '/calculator' },
    { id: 'V2', label: 'Calculator V2',           route: '/calculator-v2' },
    { id: 'V3', label: 'Calculator V3 · Preview', route: '/calculator-v3' },
  ];

  protected readonly overflowOpen = signal(false);
  protected readonly calcMenuOpen = signal(false);

  toggleOverflow(): void {
    this.calcMenuOpen.set(false);
    this.overflowOpen.update(v => !v);
  }

  closeOverflow(): void { this.overflowOpen.set(false); }
  closeCalcMenu(): void { this.calcMenuOpen.set(false); }

  closeMenus(): void {
    this.overflowOpen.set(false);
    this.calcMenuOpen.set(false);
  }

  onCalculatorDropdownClick(event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    this.overflowOpen.set(false);
    this.calcMenuOpen.update(v => !v);
  }
}
